import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

/** Same ceiling as express.json in crealo-backend app.ts. */
export const JSON_BODY_LIMIT_BYTES = 10 * 1024 * 1024;

export type PayloadTooLargeDetails = {
    payloadBytes: number;
    limitBytes: number;
};

export const measureJsonPayloadBytes = (data: unknown): number | undefined => {
    if (data == null) return undefined;
    if (typeof FormData !== "undefined" && data instanceof FormData) return undefined;
    if (typeof data === "string") return new TextEncoder().encode(data).byteLength;
    if (typeof data !== "object") return undefined;

    try {
        return new TextEncoder().encode(JSON.stringify(data)).byteLength;
    } catch {
        return undefined;
    }
};

export const formatPayloadMegabytes = (bytes: number, locale: string): string =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(bytes / (1024 * 1024));

export const jsonPayloadTooLargeDetails = (
    data: unknown,
    limitBytes = JSON_BODY_LIMIT_BYTES,
): PayloadTooLargeDetails | undefined => {
    const payloadBytes = measureJsonPayloadBytes(data);
    if (payloadBytes == null || payloadBytes <= limitBytes) return undefined;
    return { payloadBytes, limitBytes };
};

type PayloadErrorShape = {
    code?: string;
    config?: { data?: unknown; url?: string };
    response?: { status?: number; data?: Record<string, unknown> };
};

export const payloadTooLargeDetailsFromError = (error: unknown): PayloadTooLargeDetails | undefined => {
    const axiosError = error as PayloadErrorShape;
    const data = axiosError.response?.data;
    const isTooLarge =
        axiosError.response?.status === 413 ||
        data?.error === "ServerError.PayloadTooLarge" ||
        axiosError.code === "ERR_PAYLOAD_TOO_LARGE";
    if (!isTooLarge) return undefined;

    const measured = measureJsonPayloadBytes(axiosError.config?.data);
    const declared = typeof data?.payloadBytes === "number" ? data.payloadBytes : undefined;
    const received = typeof data?.receivedBytes === "number" ? data.receivedBytes : undefined;
    const payloadBytes = measured ?? declared ?? received;
    const limitBytes = typeof data?.limitBytes === "number" ? data.limitBytes : JSON_BODY_LIMIT_BYTES;
    if (payloadBytes == null) return undefined;

    return { payloadBytes, limitBytes };
};

/** Rejects an API JSON body that Express would refuse, before it is sent. */
export const rejectIfJsonPayloadTooLarge = (config: { url?: string; data?: unknown }): AxiosError | undefined => {
    const endpoint = import.meta.env.REACT_APP_API_ENDPOINT as string | undefined;
    if (!endpoint || !config.url?.startsWith(endpoint)) return undefined;

    const details = jsonPayloadTooLargeDetails(config.data);
    if (!details) return undefined;

    return new AxiosError(
        "Payload too large",
        "ERR_PAYLOAD_TOO_LARGE",
        config as InternalAxiosRequestConfig,
        undefined,
        {
            status: 413,
            statusText: "Payload Too Large",
            headers: {},
            config: config as InternalAxiosRequestConfig,
            data: {
                error: "ServerError.PayloadTooLarge",
                message: "ImportData.PayloadTooLarge",
                payloadBytes: details.payloadBytes,
                limitBytes: details.limitBytes,
            },
        },
    );
};
